import React from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {CreatedMaintenanceRecordRequest, GetMaintenanceRecordResponse} from '@di-shared/api/di/__generated';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {SestCode} from '@di-shared/features/diag/sest/const';
import useSestCodeToName from '@di-shared/features/diag/sest/useSestCodeToName';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useQueryClient, useSuspenseQuery} from '@tanstack/react-query';

interface SestDetailEngineOilAndFilterWarningProps {
  vin: string;
  sestCode: CreatedMaintenanceRecordRequest.sestCode;

  onClickMaintenanceAppointment(): void;
}

function SestDetailEngineOilAndFilterWarning({vin, sestCode, onClickMaintenanceAppointment}: SestDetailEngineOilAndFilterWarningProps) {
  const {t} = useInferableTranslation();

  const {convertSestCodeToName} = useSestCodeToName();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.maintenanceRecordController.getMostRecentMaintenanceRecord), vin, SestCode.ENGINE_OIL_AND_FILTER],
    queryFn: () => diApi.maintenanceRecordController.getMostRecentMaintenanceRecord({vin, sestCode: SestCode.ENGINE_OIL_AND_FILTER}),
  });
  return (
    <div className="section_container">
      <div className="section_container_header">
        <h3 className="title_xxsmall">{convertSestCodeToName(sestCode)}</h3>
        <button
          type="button"
          className="btn_setting"
          onClick={async () => {
            const response = await ccWebviewUtils.openBottomSheetWebViewUrl<false | null | {isChange?: boolean; historyId?: string}>(
              ROUTE_PATHS.BOTTOM_SHEET_DIAG_SEST_MANAGEMENT_SETTING.getFullPath(SestCode.ENGINE_OIL_AND_FILTER),
              {
                vin,
                replaceDate: parseDateWithFormat(data.data.replaceDate),
                replaceOdometer: data.data.replaceOdometer,
                isServiceCenterInput: data.data.source === GetMaintenanceRecordResponse.source.SERVICE_CENTER,
                historyId: data.data.id.toString(),
              }
            );

            if (!response) {
              return;
            }

            if (response.isChange) {
              await queryClient.invalidateQueries({
                queryKey: [
                  getQueryKey(diApi.maintenanceRecordController.getMostRecentMaintenanceRecord),
                  vin,
                  SestCode.ENGINE_OIL_AND_FILTER,
                ],
              });
            }
            if (response.historyId) {
              navigate(ROUTE_PATHS.DIAG_EXCHANGE_HISTORY_DETAIL_EDIT.getFullPath(vin, response.historyId));
            }
          }}
        >
          <span className="ic ic_setting_16px"></span>
        </button>
      </div>
      <div className="expendable_detail">
        <ul className="expendable_list">
          <li className="expendable_item type_warning">
            <div className="expendable_icon_box">
              <span className="ic ic_engine_oil_24px"></span>
            </div>
            <div className="expendable_info">
              <span className="expendable_info_title">{t('T30022')}</span>
              <span
                className="expendable_info_desc"
                style={{whiteSpace: 'pre-wrap'}}
              >
                {t('T30023')}
              </span>
            </div>
          </li>
        </ul>
      </div>
      <button
        type="button"
        className="btn btn_large btn_filled btn_full"
        onClick={onClickMaintenanceAppointment}
      >
        {t('T30024')}
      </button>
    </div>
  );
}

export default SestDetailEngineOilAndFilterWarning;
